import React from 'react'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import {
  StatusBarV4,
  StatusBarColorsV4,
  StatusBarStatusColorsV4,
} from '@labrav/react-components'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { SubscriptionDetailObject } from '../../../../modules/subscription/reducer'
import { getFormattedLabraDate } from '../../../../common/utils/dates'
import {
  getGcpContractStatusLabel,
  isContractEnded,
} from '../utils/subscriptionDetailHelperImports'
import { labravDefaultThemeV7 as theme } from '@labrav/react-components'

interface GcpOfferHeaderProps {
  subscriptionDetail: SubscriptionDetailObject
  accountId: string
  classes: any
}

type GcpBannerColors = {
  backgroundColor: StatusBarColorsV4
  statusColor: StatusBarStatusColorsV4
}

export const GcpOfferHeader: React.FC<GcpOfferHeaderProps> = ({
  subscriptionDetail,
  accountId,
  classes,
}) => {
  const navigate = useNavigate()
  const { cloud: routeCloud } = useParams<{ cloud?: string }>()

  const contractDetails = subscriptionDetail?.contractDetails
  if (!contractDetails) return null

  const ended = isContractEnded(
    contractDetails.contractStatus,
    contractDetails.daysLeft
  )
  const normalizedStatus = (contractDetails.contractStatus || '')
    .trim()
    .toUpperCase()
  const isExpiringSoon = normalizedStatus === 'EXPIRING_SOON'
  const formattedEndDate = contractDetails.contractEndDate
    ? getFormattedLabraDate(contractDetails.contractEndDate).formattedDate
    : ''
  const daysLeft = contractDetails.daysLeft

  const endingText = ended
    ? 'Contract ended.'
    : daysLeft != null && daysLeft > 0 && formattedEndDate
      ? `Ending in ${daysLeft} days on ${formattedEndDate}.`
      : formattedEndDate
        ? `Contract ends on ${formattedEndDate}.`
        : null

  const showBanner =
    contractDetails.contractStatus != null || endingText != null
  const bannerThemeClass = ended
    ? classes.gcpBannerThemeEnded
    : isExpiringSoon
      ? classes.gcpBannerThemeEndingSoon
      : classes.gcpBannerThemeActive
  const offerBannerBackground = '#FFDFC6' as StatusBarColorsV4
  const bannerColors: GcpBannerColors = ended
    ? {
        backgroundColor: StatusBarColorsV4.RED,
        statusColor: StatusBarStatusColorsV4.RED,
      }
    : isExpiringSoon
      ? {
          backgroundColor: offerBannerBackground,
          statusColor: '#C55B02' as StatusBarStatusColorsV4,
        }
      : {
          backgroundColor: offerBannerBackground,
          statusColor: StatusBarStatusColorsV4.GREEN,
        }

  const handleNavigateToOffers = () => {
    const cloud = routeCloud || 'gcp'
    const path = accountId
      ? `/flyout/${cloud}/${accountId}/offers-authorizations/private-offers`
      : `/flyout/offers-authorizations/private-offers`
    navigate(path)
  }

  const bannerContent = (
    <div className={classes.bannerContent}>
      {endingText && (
        <Typography className={classes.statusBannerText}>
          {endingText}
        </Typography>
      )}
      {ended && (
        <button
          className={classes.bannerCreateOfferBtn}
          onClick={handleNavigateToOffers}
          data-testid="gcp-banner-create-offer"
        >
          <Typography className={classes.bannerCreateOfferBtnText}>
            Create offer
          </Typography>
        </button>
      )}
    </div>
  )

  return (
    <div
      className={clsx(
        classes.subscriptionSummarySection,
        ended && classes.subscriptionSummarySectionEnded
      )}
    >
      {showBanner && (
        <div className={clsx(classes.gcpBannerTheme, bannerThemeClass)}>
          <StatusBarV4
            status={getGcpContractStatusLabel(contractDetails.contractStatus)}
            content={bannerContent}
            backgroundColor={bannerColors.backgroundColor}
            statusColor={bannerColors.statusColor}
            divider={!!contractDetails.contractStatus && !!endingText}
            statusChipBgColor={StatusBarColorsV4.WHITE}
          />
        </div>
      )}

      <div className={classes.actionsRow}>
        <button
          className={classes.actionButtonV2}
          onClick={handleNavigateToOffers}
          data-testid="gcp-create-private-offer"
        >
          <FontAwesomeIcon
            icon={faPlus}
            style={{ width: theme.spacing(1.5), height: theme.spacing(1.5) }}
          />
          <Typography className={classes.actionButtonText}>
            Create private offer
          </Typography>
        </button>
      </div>
    </div>
  )
}
